/**
 * Settings Page
 */
import { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { useNavigate } from 'react-router-dom';
import { User, Bell, Shield, Info, UserPlus, Users, Eye, EyeOff, X, Mail, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { userAPI } from '@/api/userAPI';
import { updateCurrentUser } from '@/store/authSlice';
import { notificationAPI } from '@/api/notificationAPI';
import { NotificationPreferences } from '@/types';
import { InviteUserModal } from './InviteUserModal';

export const Settings = () => {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Profile update form state
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileErrors, setProfileErrors] = useState<{
    firstName?: string;
    lastName?: string;
  }>({});

  // Password change form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreferences | null>(null);
  const [originalNotificationPrefs, setOriginalNotificationPrefs] = useState<NotificationPreferences | null>(null);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [notificationPrefError, setNotificationPrefError] = useState<string | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Update local state when user data changes
  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
    }
  }, [user]);

  useEffect(() => {
    const loadNotificationPreferences = async () => {
      try {
        setIsLoadingPreferences(true);
        setNotificationPrefError(null);
        const prefs = await notificationAPI.getPreferences();
        setNotificationPrefs(prefs);
        setOriginalNotificationPrefs(prefs);
      } catch (error: any) {
        const message = error?.response?.data?.detail || 
                       error?.message || 
                       'Unable to load notification preferences.';
        setNotificationPrefError(message);
        toast.error(message);
      } finally {
        setIsLoadingPreferences(false);
      }
    };

    loadNotificationPreferences();
  }, []);

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  type PreferenceKey = 'email_notifications' | 'task_assignments' | 'project_updates' | 'task_comments' | 'task_updates';
  const preferenceKeys: PreferenceKey[] = ['email_notifications', 'task_assignments', 'project_updates', 'task_comments', 'task_updates'];

  const handlePreferenceToggle = (key: PreferenceKey, value: boolean) => {
    if (!notificationPrefs) return;
    setNotificationPrefs({ ...notificationPrefs, [key]: value });
  };

  const hasPreferenceChanges =
    !!notificationPrefs &&
    !!originalNotificationPrefs &&
    preferenceKeys.some((key) => notificationPrefs[key] !== originalNotificationPrefs[key]);

  const preferenceFormDisabled = isLoadingPreferences || !notificationPrefs;

  const handleSavePreferences = async () => {
    if (!notificationPrefs) return;
    setIsSavingPreferences(true);
    setNotificationPrefError(null);
    try {
      const payload = {
        email_notifications: notificationPrefs.email_notifications,
        task_assignments: notificationPrefs.task_assignments,
        project_updates: notificationPrefs.project_updates,
        task_updates: notificationPrefs.task_updates,
        task_comments: notificationPrefs.task_comments,
      };
      const updated = await notificationAPI.updatePreferences(payload);
      setNotificationPrefs(updated);
      setOriginalNotificationPrefs(updated);
      toast.success('Notification preferences updated successfully');
    } catch (error: any) {
      const message =
        error?.response?.data?.detail || 
        error?.message ||
        'Failed to update notification preferences. Please try again.';
      setNotificationPrefError(message);
      toast.error(message);
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const validateProfileForm = (): boolean => {
    const errors: typeof profileErrors = {};
    let isValid = true;

    if (!firstName.trim()) {
      errors.firstName = 'First name is required';
      isValid = false;
    } else if (firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
      isValid = false;
    } else if (firstName.trim().length > 100) {
      errors.firstName = 'First name must be less than 100 characters';
      isValid = false;
    }else if (/^\d+$/.test(firstName)) {
      errors.firstName = 'First Name cannot be only numbers';
      isValid = false;
    }

    if (!lastName.trim()) {
      errors.lastName = 'Last name is required';
      isValid = false;
    } else if (lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
      isValid = false;
    } else if (lastName.trim().length > 100) {
      errors.lastName = 'Last name must be less than 100 characters';
      isValid = false;
    }else if (/^\d+$/.test(lastName)) {
      errors.lastName = 'Last Name cannot be only numbers';
      isValid = false;
    }

    setProfileErrors(errors);
    return isValid;
  };

  /**
   * 🚀 Handle profile update
   */
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateProfileForm()) return;

    setIsUpdatingProfile(true);
    setProfileErrors({});

    try {
      const updateData = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        display_name: `${firstName.trim()} ${lastName.trim()}`.trim()
      };

      await dispatch(updateCurrentUser(updateData)).unwrap();

      toast.success('Profile updated successfully');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        'Failed to update profile. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsUpdatingProfile(false);
    }
  };
  const validatePasswordForm = (): boolean => {
    const errors: typeof passwordErrors = {};
    let isValid = true;

    if (!currentPassword.trim()) {
      errors.currentPassword = 'Current password is required';
      isValid = false;
    }

    if (!newPassword.trim()) {
      errors.newPassword = 'New password is required';
      isValid = false;
    } else if (newPassword.length < 8) {
      errors.newPassword = 'New password must be at least 8 characters';
      isValid = false;
    } else if (newPassword.length > 72) {
      errors.newPassword = 'New password must be less than 72 characters';
      isValid = false;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      errors.newPassword =
        'Password must contain uppercase, lowercase, and a number';
      isValid = false;
    }

    if (!confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your new password';
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    if (currentPassword === newPassword && newPassword.trim() !== '') {
      errors.newPassword = 'New password must be different from current password';
      isValid = false;
    }

    setPasswordErrors(errors);
    return isValid;
  };

  /**
   * 🚀 Handle password change
   */
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordForm()) return;

    setIsChangingPassword(true);
    setPasswordErrors({});

    try {
      await userAPI.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      toast.success('Password changed successfully');

      // Reset form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        'Failed to change password. Please try again.';

      // Handle incorrect current password
      if (
        error?.response?.status === 401 ||
        errorMessage.toLowerCase().includes('current password') ||
        errorMessage.toLowerCase().includes('incorrect')
      ) {
        setPasswordErrors({ currentPassword: 'Current password is incorrect' });
        toast.error('Current password is incorrect');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  /**
   * 🧠 Real-time validation for new password
   */
  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    const errors: typeof passwordErrors = { ...passwordErrors };

    if (value.length < 8) {
      errors.newPassword = 'New password must be at least 8 characters';
    } else if (value.length > 72) {
      errors.newPassword = 'New password must be less than 72 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
      errors.newPassword =
        'Password must contain uppercase, lowercase, and a number';
    } else {
      errors.newPassword = undefined;
    }

    if (confirmPassword && value !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    } else {
      errors.confirmPassword = undefined;
    }

    setPasswordErrors(errors);
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="space-y-6 pt-2">
      <InviteUserModal isOpen={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} />
      {/* Header */}
      {/* <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account settings</p>
      </div> */}
      <div className="sticky top-0 z-10 bg-gray-50 pb-2 pt-1">
      <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
      <p className="text-gray-600 mt-1">Manage your account settings</p>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1 self-start sticky top-20">
          <div className="card">
          <nav className="space-y-1">
            <button onClick={() => scrollTo('profile')} className="flex items-center gap-3 px-4 py-3 text-primary-600 bg-primary-50 rounded-lg w-full text-left">
              <User className="w-5 h-5" />
              <span className="font-medium">Profile</span>
            </button>
            <button onClick={() => scrollTo('notifications')} className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg w-full text-left">
              <Bell className="w-5 h-5" />
              <span>Notifications</span>
            </button>
            <button onClick={() => scrollTo('security')} className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg w-full text-left">
              <Shield className="w-5 h-5" />
              <span>Security</span>
            </button>
            <button onClick={() => scrollTo('about')} className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg w-full text-left">
              <Info className="w-5 h-5" />
              <span>About</span>
            </button>
            {isAdmin && (
              <>
                <button onClick={() => navigate('/onboard-user')} className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg w-full text-left">
                  <UserPlus className="w-5 h-5" />
                  <span>Onboard User</span>
                </button>
                <button onClick={() => navigate('/create-team')} className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg w-full text-left">
                  <Users className="w-5 h-5" />
                  <span>Create Team</span>
                </button>
                <button
                onClick={() => setIsInviteModalOpen(true)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg w-full text-left">
                <Mail className="w-5 h-5" />
                <span>Invite User</span>
                </button>
              </>
            )}
          </nav>

          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Profile Section */}
          <div id="profile" className="card mb-6 scroll-mt-16">
            <h2 className="text-xl font-semibold mb-4">Profile Information</h2>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (profileErrors.firstName) {
                        setProfileErrors({
                          ...profileErrors,
                          firstName: undefined,
                        });
                      }
                    }}
                    className={`input ${profileErrors.firstName ? 'border-red-500' : ''}`}
                    placeholder="Enter your first name"
                    disabled={isUpdatingProfile}
                  />
                  {profileErrors.firstName && (
                    <p className="mt-1 text-sm text-red-600">
                      {profileErrors.firstName}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      if (profileErrors.lastName) {
                        setProfileErrors({
                          ...profileErrors,
                          lastName: undefined,
                        });
                      }
                    }}
                    className={`input ${profileErrors.lastName ? 'border-red-500' : ''}`}
                    placeholder="Enter your last name"
                    disabled={isUpdatingProfile}
                  />
                  {profileErrors.lastName && (
                    <p className="mt-1 text-sm text-red-600">
                      {profileErrors.lastName}
                    </p>
                  )}
                </div>
              </div>

              {/* Full Name Display */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={`${firstName.trim()} ${lastName.trim()}`.trim() || user?.name || ''}
                  readOnly
                  className="input bg-gray-50"
                  placeholder="Full name will be generated from first and last name"
                />
                {/* <p className="mt-1 text-xs text-gray-500">
                  This is automatically generated from your first and last name
                </p> */}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  readOnly
                  className="input bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <input
                  type="text"
                  value={user?.role || ''}
                  readOnly
                  className="input bg-gray-50"
                />
              </div>

              <button 
                type="submit"
                className="btn btn-primary"
                disabled={
                  isUpdatingProfile ||
                  !firstName.trim() ||
                  !lastName.trim() ||
                  (firstName.trim() === user?.first_name && lastName.trim() === user?.last_name)
                }
              >
                {isUpdatingProfile ? 'Updating Profile...' : 'Update Profile'}
              </button>
            </form>
          </div>

          {/* Notifications Section */}
          <div id="notifications" className="card mb-6 scroll-mt-16">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold">Notification Preferences</h2>
                <p className="text-sm text-gray-500">Choose what arrives in your inbox</p>
              </div>
              {isLoadingPreferences && <span className="text-xs text-gray-500">Loading...</span>}
            </div>

            {notificationPrefs ? (
              <>
                <div className="space-y-3">
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4"
                      checked={notificationPrefs.email_notifications}
                      onChange={(e) => handlePreferenceToggle('email_notifications', e.target.checked)}
                      disabled={isSavingPreferences || isLoadingPreferences}
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Email notifications</span>
                      <p className="text-xs text-gray-500">Master switch for all email alerts</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4"
                      checked={notificationPrefs.task_assignments}
                      onChange={(e) => handlePreferenceToggle('task_assignments', e.target.checked)}
                      disabled={
                        preferenceFormDisabled || isSavingPreferences || !notificationPrefs.email_notifications
                      }
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Task assignments</span>
                      <p className="text-xs text-gray-500">Get an email whenever you are assigned to a task</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4"
                      checked={notificationPrefs.project_updates}
                      onChange={(e) => handlePreferenceToggle('project_updates', e.target.checked)}
                      disabled={
                        preferenceFormDisabled || isSavingPreferences || !notificationPrefs.email_notifications
                      }
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Status / project updates</span>
                      <p className="text-xs text-gray-500">Alerts when tracked tasks move between statuses</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4"
                      checked={notificationPrefs.task_updates}
                      onChange={(e) => handlePreferenceToggle('task_updates', e.target.checked)}
                      disabled={
                        preferenceFormDisabled || isSavingPreferences || !notificationPrefs.email_notifications
                      }
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Task updates</span>
                      <p className="text-xs text-gray-500">Get emails when task details change (labels, due date, time logs, description, etc.)</p>
                    </div>
                  </label>


                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4"
                      checked={notificationPrefs.task_comments}
                      onChange={(e) => handlePreferenceToggle('task_comments', e.target.checked)}
                      disabled={
                        preferenceFormDisabled || isSavingPreferences || !notificationPrefs.email_notifications
                      }
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Comments on my tasks</span>
                      <p className="text-xs text-gray-500">Stay in the loop when teammates leave comments</p>
                    </div>
                  </label>
                </div>

                {notificationPrefError && (
                  <p className="text-sm text-red-600 mt-3">{notificationPrefError}</p>
                )}

                <button
                  type="button"
                  className="btn btn-primary mt-4"
                  disabled={isSavingPreferences || preferenceFormDisabled || !hasPreferenceChanges}
                  onClick={handleSavePreferences}
                >
                  {isSavingPreferences ? 'Saving...' : 'Save Preferences'}
                </button>
              </>
            ) : (
              !isLoadingPreferences && (
                <p className="text-sm text-gray-500">
                  {notificationPrefError || 'Notification preferences are currently unavailable.'}
                </p>
              )
            )}
          </div>

          {/* Security Section */}
          <div id="security" className="card mb-6 scroll-mt-16">
            <h2 className="text-xl font-semibold mb-4">Security</h2>
            <form onSubmit={handleChangePassword} className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword.current ? 'text' : 'password'}
                    className={`input pr-10 ${passwordErrors.currentPassword ? 'border-red-500' : ''
                      }`}
                    placeholder="••••••••"
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      if (passwordErrors.currentPassword) {
                        setPasswordErrors({
                          ...passwordErrors,
                          currentPassword: undefined,
                        });
                      }
                    }}
                    disabled={isChangingPassword}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                    tabIndex={-1}
                  >
                    {showPassword.current ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {passwordErrors.currentPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {passwordErrors.currentPassword}
                  </p>
                )}
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword.new ? 'text' : 'password'}
                    className={`input pr-10 ${passwordErrors.newPassword ? 'border-red-500' : ''
                      }`}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => handleNewPasswordChange(e.target.value)}
                    disabled={isChangingPassword}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                    tabIndex={-1}
                  >
                    {showPassword.new ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {passwordErrors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {passwordErrors.newPassword}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword.confirm ? 'text' : 'password'}
                    className={`input pr-10 ${passwordErrors.confirmPassword ? 'border-red-500' : ''
                      }`}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      const errors: typeof passwordErrors = { ...passwordErrors };
                      if (e.target.value && newPassword && e.target.value !== newPassword) {
                        errors.confirmPassword = 'Passwords do not match';
                      } else {
                        errors.confirmPassword = undefined;
                      }
                      setPasswordErrors(errors);
                    }}
                    disabled={isChangingPassword}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                    tabIndex={-1}
                  >
                    {showPassword.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {passwordErrors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {passwordErrors.confirmPassword}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  isChangingPassword ||
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword
                }
              >
                {isChangingPassword ? 'Changing Password...' : 'Change Password'}
              </button>
            </form>
          </div>


          {/* About Section */}
          <div id="about" className="card mb-6 scroll-mt-16">
            <h2 className="text-xl font-semibold mb-4">About</h2>
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                <strong>Version:</strong> 1.0.0
              </p>
              <p className="pt-4 border-t border-gray-200 mt-4">
                © 2024 Project Tracker. All rights reserved.
              </p>
            </div>
          </div>
          <div className="h-96" />
        </div>
      </div>
    </div>
  );
};
